import React, { useEffect, useState, useMemo, forwardRef, useCallback, useRef } from 'react';
import { isEmpty, map ,includes, compact, noop, forEach, isString, split, find, isNil } from 'lodash';
import { Form, Button } from 'antd'
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import { FieldTypes, parseSelectOptions , generateFormFields} from 'utils/formUtils';
import message from "components/CustomMessage";
import GenericMappingForm from '../GenericMappingForm';
import { SNYK_CATEGORY } from '../new/util';
import { useFeatureFlag } from 'contexts/FeatureFlagContext';

const initialFormValues = {
  product: undefined,
  subProduct: undefined,
  environment: undefined,
  configType: 'projects',
  snykProjectList: [{ project: undefined }]
};

const DELIMITER = ";";

const SnykMappingForm = forwardRef(({disableProjectFields = false, ...props}, ref) => {
  const featureFlags = useFeatureFlag();
  const [collectionList, setCollectionList] = useState(() => {
    if (props.record && (!isNil(props.record.project_type) || props.record.project_type === 'collections')) {
      const { projectId, projectName } = props.record;
      const projectIdArray = projectId.split(',');
      const projectNameArray = projectName.split(',');
      return projectIdArray.map((id, index) => ({id, name: projectNameArray[index]}))
    }
    return [];
  });
  const [initialValues] = useState(() => {
    if (props.record) {
      return [];
    }
    return [
      {
        ...initialFormValues,
        product: props.defaultProduct,
        subProduct: props.defaultSubProduct,
        subProductId: props.defaultSubProductId,
        environment: props.defaultEnvironment,
      },
    ];
  });
  const editModeRef = useRef(!isEmpty(props.record));
  const referenceOptions = useRef([])
  const [projectList, setProjectList] = useState(() => {
    if (props.record && (isNil(props.record.project_type) || (props.record.project_type === 'project'))) {
      const { orgId , projectId, targetId, origin } = props.record;
      const projectIdArray = projectId.split(',');
      const targetIdArray = isEmpty(targetId) ? []: targetId.split(',')
      const originArray = isEmpty(origin)?[]:origin.split(",");
      const projects = map(projectIdArray, (projectWithReference, i) => {
        const index = projectWithReference.indexOf(DELIMITER);
        if(index !== -1){
          const project = projectWithReference.substring(0, index);
          const references = projectWithReference.substring(index + 1, projectWithReference.length);
          return { project, references: split(references.replace(/;/g, ','), ',') , targetId: targetIdArray[i]};
        }
        return { project: projectWithReference }
      });

      return {
        [orgId]: projects.map(({ project, targetId,references },i) => ({id: project, name: project , otherProperties: { references, targetId, origin:originArray[i]} }))
      }
    }
    return {};
  });
  const [organisationList,setOrganisationList] = useState(() => {
    if (props.record) {
      const { orgId , orgName } = props.record;
      return [{id: orgId, name: orgName }]
    }
    return [];
  });
  const [organisation,setOrganisation] = useState(()=>{
    if (props.record) {
      const { orgId  } = props.record;
      return orgId;
    }
    return '';
  });
  const [exploitOptions, setExploitOptions] = useState(() => {
    if (props.record) {
      const { exploitMaturity } = props.record;
      return exploitMaturity?.split(',');
    }
    return [];
  });
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId });
    promise.then((response) => {
      const { projects } = response;
      setOrganisationList(projects);
    }).catch(() => {
      message.error('Problem fetching projects')
    });

    return cancel;
  }, [props.loginConfigId,props.toolName]);


  const fetchProjects =  useCallback((organisation) =>{
    if((editModeRef.current && organisation) || (!projectList[organisation] && organisation)){
        editModeRef.current = false
        const {promise,cancel} = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, 
        { login_id: props.loginConfigId, organisation});

        promise.then(response=>{
          const { projects } = response;
          setProjectList((projectList) =>({...projectList,[organisation]:projects}));
        }).catch(()=>{
        message.error('Problem fetching projects')
        })

        return cancel;
      }
  },[props.loginConfigId,props.toolName,projectList])

  const fetchCollection =  useCallback(() =>{
    if((editModeRef.current && organisation) || organisation){
        editModeRef.current = false
        const {promise,cancel} = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, 
        { login_id: props.loginConfigId, organisation, type: 'collections' });

        promise.then(response=>{
          const { projects } = response;
            setCollectionList(projects)
        }).catch(()=>{
        message.error('Problem fetching projects')
        })

        return cancel;
      }
  },[props.loginConfigId,props.toolName,organisation])

  useEffect(()=>{
    fetchProjects(organisation);
  },[organisation,fetchProjects]);

  useEffect(() => {
    fetchCollection();
  },[fetchCollection]);

  useEffect(() => {
    setLoading(true)
    const { promise, cancel } = ToolsIntegrationsAPI.getSnykExploitOptions()
    promise.then(res => setExploitOptions(res)).catch((err) => console.log(err)).finally(() => setLoading(false));
    return cancel
  }, []);

  const parseExtraFieldsDataFromResponse = useCallback((config) => {
        const { projectId, exploitMaturity, scanType ,targetId, project_type} = config;
        let projects = [{ project: undefined }]
        if(isEmpty(project_type) || (project_type === 'project')){
          const targetIdArray = isEmpty(targetId) ? []: targetId.split(",");
          if(isEmpty(targetId)) message.error('Target not found.')
          const projectWithRefrences = projectId?.split(',');
          projects = map(projectWithRefrences, (projectWithReference,i) => {
            const index = projectWithReference.indexOf(DELIMITER);
            if(index !== -1){
              const project = featureFlags.SNYK_TARGET_ORIGIN ? targetIdArray[i] : projectWithReference.substring(0, index);
              const references = projectWithReference.substring(index + 1, projectWithReference.length);
              return { project, references: split(references.replace(/;/g, ','), ',') };
            }
            return { project: featureFlags.SNYK_TARGET_ORIGIN ? targetIdArray[i] : projectWithReference }
          });
        }
        return ({
          snykProjectList: projects,
          collection: isEmpty(project_type) || (project_type === 'project') ? undefined : projectId.split(','),
          exploitMaturity: isEmpty(exploitMaturity) ? undefined : exploitMaturity?.split(','),
          scanType: isEmpty(scanType) ? undefined : scanType.split(','),
          configType: isEmpty(project_type) || (project_type === 'project')  ? 'projects' : 'collections'
        });
    }, [featureFlags.SNYK_TARGET_ORIGIN]);

  const onOrganisationChange = useCallback((value, field) => {
    setOrganisation(value)
    const { name } = field;
    const configurations = form.getFieldValue("configurations");
    const updatedConfiguration = [
      ...configurations,
    ];
    updatedConfiguration[name] = {
      ...updatedConfiguration[name],
      snykProjectList: [{ project: undefined }]
    }
    form.setFieldsValue({ configurations: updatedConfiguration });
  }, [form]);

  const getExtraFieldsData = useCallback((config) => {
    const { snykProjectList, orgId, exploitMaturity, scanType, collection, configType } = config;
    const targetId =[];
    const origins =[];
    const isEmptyList = [];
    let projectId = [];
    let collectionData = [];
    if(configType !== 'collections'){
     projectId = map(snykProjectList, ({ project, references },i) => {
        if(featureFlags.SNYK_TARGET_ORIGIN){
          const _targetId = project;
          const targetProject = projectList[orgId].filter(p => p?.otherProperties?.targetId === project)[0]
          const origin = targetProject?.otherProperties.origin;
          const isEmptyProperty = targetProject?.otherProperties.isEmpty ?? null;
          isEmptyList.push(isEmptyProperty === null ? 'null' : isEmptyProperty);
          origins.push(origin);
          targetId.push(_targetId)
          references = Array.isArray(references) ? references.join(';') : references
          return references ? targetProject?.name + DELIMITER + references: targetProject?.name
        }else{
          const targetProject = find(projectList[orgId], ['name', project])
          const _targetId = targetProject?.otherProperties.targetId; 
          const isEmptyProperty = targetProject?.otherProperties.isEmpty ?? null;
          isEmptyList.push(isEmptyProperty === null ? 'null' : isEmptyProperty);
          targetId.push(_targetId)
          references = Array.isArray(references) ? references.join(';') : references
          return references ? project + DELIMITER + references: project
        }
      });
    }else {
      collectionData = collectionList.reduce((acc,cv) =>{
        const { id, name } = cv;
        if(collection.includes(id)){
          acc.id.push(id);
          acc.name.push(name);
        }
        return acc;
      },{id:[],name:[]})
    }
    
    return ({
      projectId: configType === 'collections' ? collectionData.id.join() : projectId.join(),
      projectName: configType === 'collections' ? collectionData.name.join() :projectId.join(),
      origin:origins.join(),
      orgId,
      orgName: organisationList.find(org => org.id === orgId)?.name,
      isEmptyList: isEmptyList.join(),
      exploitMaturity: isEmpty(exploitMaturity) ? [] : exploitMaturity.join(),
      scanType: isEmpty(scanType) ? [] : scanType.join(),
      project_type: configType === 'collections' ? 'collections' : 'project',
      ...(!isEmpty(compact(targetId)) && { targetId: targetId.join() }),
      //send the property received in config
      ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id })
    });
  },[organisationList, projectList, collectionList, featureFlags]);


  const snykProjectOptions = useCallback((projects, listField)=>{
    const selected = compact(map(form.getFieldsValue().configurations?.[listField.name]?.snykProjectList, p => p?.project));
    return map(projects , p => ({ ...p , ...((featureFlags.SNYK_TARGET_ORIGIN && p.otherProperties?.origin)?{label:`[${p.otherProperties?.origin}] `+p?.label, value: p.otherProperties?.targetId, disabled: includes(selected , p?.otherProperties?.targetId) }:{ disabled: includes(selected, p.id) })  }));
  },[form,featureFlags.SNYK_TARGET_ORIGIN])

  const findProjectByName = useCallback((project, name)=> {
    for (const key in project) {
        if ((featureFlags.SNYK_TARGET_ORIGIN && project[key]?.otherProperties?.targetId === name) || project[key]?.name === name) {
            return project[key];
        }
    }
    return null;
 },[featureFlags.SNYK_TARGET_ORIGIN]);
 
 const onValuesChange = useCallback((values, ...args) => {
  if(values.configurations){
    let isProjectChanged = false;
      const formValues = form.getFieldsValue();
      forEach(values.configurations, (configs, index) => {
        
        // since this is a list field, onchange will trigger only for the changed row.
        // if change is done at 2nd row, data received in this callback will only contain 2nd row data
        // configs.snykProjectList is given as string when we search in the field
        if(!configs || !configs.snykProjectList || isString(configs.snykProjectList)){
          return;
        }
        forEach(configs.snykProjectList, (object, i) => {

          // since this is a list field, onchange will trigger only for the changed row.
          // if change is done at 2nd row, data received in this callback will only contain 2nd row data
          // Also checking if the object contains `project` and nothing else 
          // because we need to reset the references field only if the project is changed
          // and this change is also triggered when rows are added or deleted, so we need to ignore it
          if(object && object.project && Object.keys(object).length === 1){
            isProjectChanged = true;
            formValues.configurations[index].snykProjectList[i].references = undefined;
          }
        })
      });
      if(isProjectChanged){
        form.setFieldsValue(formValues);
      }
  }
}, [form]);

  const projectListRender = useCallback((listField) => {
    const projects = snykProjectOptions(parseSelectOptions(projectList[organisation]), listField);
    
    let refOptionAPI = referenceOptions.current[listField.name];
    
    if(!refOptionAPI){
      refOptionAPI = referenceOptions.current[listField.name] = ({ listFieldName: nestedListFieldName, ...params }) => {
        const allConfigs = form.getFieldsValue().configurations;
        const project = allConfigs[listField.name]?.snykProjectList[nestedListFieldName]?.project;
        if (!project) {
          return { promise: Promise.resolve([]), cancel: noop };
        }

       const refObj = findProjectByName(params, project);
       const refrenceArray = (isEmpty(refObj) || isEmpty(refObj.otherProperties.references)) ? [] : refObj.otherProperties.references;
      return { promise: Promise.resolve(refrenceArray), cancel: noop };
      }
    }

    const __fields = [{
      name: 'project',
      type: FieldTypes.SELECT,
      disabled: isEmpty(projects) || disableProjectFields,
      loading: organisation && isEmpty(projectList),
      colSpan: 10,
      options: projects,
      required: true,
    }, {
      name: 'references',
      placeholder: "Leave blank to select all",
      type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
      disabled: isEmpty(projects) || disableProjectFields,
      colSpan: 10,
      props: {
        mode:"multiple",
        api: refOptionAPI,
        apiParams: projects,
        setExtraProps: (field, nestedListField) => {
          const allConfigs = form.getFieldsValue().configurations;
          const project = allConfigs[listField.name]?.snykProjectList[nestedListField.name]?.project;
          return {
            showValueOnload: false,
            resetOptionsProp: project
          }
        }
      }
    },
    {
      name: "add",
      colSpan: 2,
      render: ({ remove, name, fields }) => {
        return <Button disabled={fields.length === 1} type='dashed' onClick={() => { remove(name) }}>-</Button>;
      }
    }]

    const projectFields  = [
      {
        name: "label",
        type: FieldTypes.LABEL,
        value: <b className="required-mark">Project</b>,
        style: { marginBottom: -24 },
        colSpan: 10,
      },
      {
        name: "label",
        type: FieldTypes.LABEL,
        value: <b>References</b>,
        style: { marginBottom: -24 },
        colSpan: 10,
      }
    ]

    const collectionFields = [
      {
        name: [listField.name, "collection"],
        type: FieldTypes.MULTI_SELECT,
        label: 'Collection',
        disabled: isEmpty(collectionList) || disableProjectFields,
        loading: organisation && isEmpty(collectionList),
        colSpan: 10,
        options: collectionList,
        required: true,
      },
      {
        colSpan: 10,
        type: FieldTypes.LABEL
      }
    ]

    const selectedType = form.getFieldsValue().configurations[listField.name]?.configType;
  
    return (
      <div className='m-b-sm'>
        {
          selectedType === 'projects'
          ?
          <>
          {generateFormFields(projectFields)}
          <Form.List name={[listField.name, "snykProjectList"]}>
          {(fields, { add, remove }) => {
            return (
              <div>
                {map(fields, field => {
                  return generateFormFields(__fields, 10, { ...field, add, remove, fields }, field.name);
                })}
                <Button size='small' type='dashed' onClick={() => add({ project: undefined })} disabled={disableProjectFields}>+ Add Project</Button>
              </div>
            )
          }}
          </Form.List>
        </>
        :
          generateFormFields(collectionFields)
        }
      </div>
    )
  }, [disableProjectFields, form, projectList,collectionList, organisation, snykProjectOptions, findProjectByName]);

  const extraFields = useMemo(() => ([
    {
      name: 'configType',
      type: FieldTypes.RADIO,
      options: [{label: "Projects", value: "projects" }, {label: "Collection", value: "collections" }],
      disabled: disableProjectFields,
    },
    {
      name: 'orgId',
      colSpan: '10',
      type: FieldTypes.SELECT,
      label: 'Organization',
      options: parseSelectOptions(organisationList),
      required: true,
      disabled: disableProjectFields,
      props: {
        onChange: onOrganisationChange,
        autoFocus: !disableProjectFields
      }
    },
    {
      type: FieldTypes.LABEL,
      colSpan: 14
    },
    {
      name: (listField) => [listField.name, "snykProjectList"],
      render: projectListRender
    },
    {
      label: 'Scan Type',
      name: 'scanType',
      colSpan: 10,
      type: FieldTypes.MULTI_SELECT,
      options: SNYK_CATEGORY,
    },
    {
      name: "exploitMaturity",
      colSpan: 13,
      type: FieldTypes.MULTI_SELECT,
      label: "Exploit Maturity",
      loading: loading,
      options: exploitOptions,
    }
  ]), [loading, onOrganisationChange, organisationList, disableProjectFields, projectListRender, exploitOptions]);

  return (
    <GenericMappingForm
      form={form}
      ref={ref}
      getExtraFieldsData={getExtraFieldsData}
      extraFields={extraFields}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
      formDefaultValues={initialValues}
      newRowDefaultValue={initialValues[0]}
      onValuesChange={onValuesChange}
      {...props}
    />
  )
})

export default SnykMappingForm;
